import { getLocalizedAgentName, getLocalizedAgentDesc, getLocalizedAgentCategory, getLocalizedAgentFeatures, formatPriceWithCurrency, Currency, Language, Jurisdiction } from '../utils/i18n';
import React, { useState, useEffect } from 'react';
import { 
  Globe, ArrowLeft, ArrowRight, Sparkles, ShieldCheck, FileCheck,
  CreditCard, Landmark, CheckCircle2, UserCheck, Briefcase, FileSpreadsheet,
  Search, Filter, Zap, Scale, FileCode, Building2, SearchCheck, ArrowLeftRight
} from 'lucide-react';
import * as Icons from 'lucide-react';
import agentsConfig from '../agentsConfig.json';
import { RegionalPaymentModal } from './RegionalPaymentModal';

interface CrossBorderCenterHubViewProps {
  onLaunchAgent: (target: string, preferredLang?: Language) => void;
  onBack: () => void;
  language: Language;
  currency?: Currency;
  currentUserEmail?: string;
  onSelectLanguage?: (lang: Language) => void;
  onSelectCurrency?: (cur: Currency) => void;
}

export const CROSS_BORDER_AGENT_IDS = [
  'cross-border-contract-harmonizer',
  'cross-border-vat-invoicing-gateway',
  'cross-border-corporate-kyc-matrix',
  'cross-border-registry-connector',
  'eu-customs-sad-validator',
  'cbam-carbon-tax-calculator',
  'pharma-ectd-dossier-validator',
  'gafta-fosfa-arbitration-lawyer',
  'espd-jedz-procurement-generator'
];

const AgentIcon = ({ name, className }: { name: string; className?: string }) => {
  const IconComponent = (Icons as any)[name];
  if (!IconComponent) return <Globe className={className} />;
  return <IconComponent className={className} />;
};

export default function CrossBorderCenterHubView({
  onLaunchAgent,
  onBack,
  language,
  currency = 'EUR',
  currentUserEmail,
  onSelectLanguage,
  onSelectCurrency
}: CrossBorderCenterHubViewProps) {
  const [activeLang, setActiveLang] = useState<Language>(language || 'uk');
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPaymentAgent, setSelectedPaymentAgent] = useState<{ id: string; name: string; price: number; singlePrice: number } | null>(null);

  // Cross-Border Jurisdiction Matrix State
  const [sourceCountry, setSourceCountry] = useState<string>('UA');
  const [targetCountry, setTargetCountry] = useState<string>('PL');

  const handleOpenPayment = (id: string, name: string, price: number, singlePrice: number) => {
    setSelectedPaymentAgent({ id, name, price, singlePrice });
    setPaymentModalOpen(true);
  };

  useEffect(() => {
    if (language) setActiveLang(language);
  }, [language]);

  const isUk = activeLang === 'uk';
  const isPl = activeLang === 'pl';
  const isEn = activeLang === 'en';
  const isDe = activeLang === 'de';
  const isFr = activeLang === 'fr';
  const isIt = activeLang === 'it';
  const isEs = activeLang === 'es';
  const isPt = activeLang === 'pt';
  const isSv = activeLang === 'sv';

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [pricing, setPricing] = useState<Record<string, { price: number; limit: number; durationDays: number; singlePrice: number }>>({});

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await fetch('/api/subscription/pricing');
        if (res.ok) {
          const data = await res.json();
          const pMap: Record<string, { price: number; limit: number; durationDays: number; singlePrice: number }> = {};
          if (data.pricing && Array.isArray(data.pricing)) {
            data.pricing.forEach((p: any) => {
              pMap[p.agentId] = {
                price: p.price,
                limit: p.limit,
                durationDays: p.durationDays,
                singlePrice: p.singlePrice || Math.round(p.price * 0.2)
              };
            });
          }
          setPricing(pMap);
        }
      } catch (err) {
        console.error('Failed to fetch pricing:', err);
      }
    };
    fetchPricing();
  }, []);

  const crossBorderAgents = agentsConfig.filter(a => CROSS_BORDER_AGENT_IDS.includes(a.id));

  const filteredAgents = crossBorderAgents.filter(agent => {
    const name = getLocalizedAgentName(agent, activeLang).toLowerCase();
    const desc = getLocalizedAgentDesc(agent, activeLang).toLowerCase();
    const q = searchQuery.toLowerCase();
    const matchesSearch = name.includes(q) || desc.includes(q);

    if (selectedCategory === 'all') return matchesSearch;
    if (selectedCategory === 'contracts') return matchesSearch && (agent.id.includes('contract') || agent.id.includes('arbitration'));
    if (selectedCategory === 'vat_invoicing') return matchesSearch && (agent.id.includes('vat') || agent.id.includes('invoicing') || agent.id.includes('customs') || agent.id.includes('cbam'));
    if (selectedCategory === 'corporate') return matchesSearch && (agent.id.includes('corporate') || agent.id.includes('kyc') || agent.id.includes('registry'));
    if (selectedCategory === 'trade_procurement') return matchesSearch && (agent.id.includes('procurement') || agent.id.includes('pharma') || agent.id.includes('gafta'));
    return matchesSearch;
  });

  const COUNTRIES = [
    { code: 'UA', nameUk: 'Україна (UA)', nameEn: 'Ukraine (UA)', namePl: 'Ukraina (UA)', flag: '🇺🇦' },
    { code: 'PL', nameUk: 'Польща (PL)', nameEn: 'Poland (PL)', namePl: 'Polska (PL)', flag: '🇵🇱' },
    { code: 'DE', nameUk: 'Німеччина (DE)', nameEn: 'Germany (DE)', namePl: 'Niemcy (DE)', flag: '🇩🇪' },
    { code: 'FR', nameUk: 'Франція (FR)', nameEn: 'France (FR)', namePl: 'Francja (FR)', flag: '🇫🇷' },
    { code: 'IT', nameUk: 'Італія (IT)', nameEn: 'Italy (IT)', namePl: 'Włochy (IT)', flag: '🇮🇹' },
    { code: 'ES', nameUk: 'Іспанія (ES)', nameEn: 'Spain (ES)', namePl: 'Hiszpania (ES)', flag: '🇪🇸' },
    { code: 'PT', nameUk: 'Португалія (PT)', nameEn: 'Portugal (PT)', namePl: 'Portugalia (PT)', flag: '🇵🇹' },
    { code: 'SE', nameUk: 'Швеція (SE)', nameEn: 'Sweden (SE)', namePl: 'Szwecja (SE)', flag: '🇸🇪' },
    { code: 'US', nameUk: 'США (US)', nameEn: 'USA (US)', namePl: 'USA (US)', flag: '🇺🇸' },
    { code: 'UK', nameUk: 'Велика Британія (UK)', nameEn: 'United Kingdom (UK)', namePl: 'Wielka Brytania (UK)', flag: '🇬🇧' }
  ];

  return (
    <div className="space-y-8 animate-fade-in font-mono text-slate-900 pb-16">
      {/* Header Banner */}
      <div className="relative overflow-hidden bg-gradient-to-br from-indigo-950 via-slate-900 to-slate-950 border-2 border-indigo-500/30 rounded-3xl p-6 sm:p-10 shadow-2xl">
        <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl pointer-events-none -ml-20 -mb-20" />

        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-500/20 border border-indigo-400/40 text-indigo-300 text-xs font-bold uppercase tracking-wider">
              <Globe className="w-4 h-4 text-indigo-400 animate-spin-slow" />
              <span>
                {isUk ? '🌐 ОПЕРАЦІЙНИЙ ЦЕНТР ТРАНСКОРДОННОЇ ІНТЕГРАЦІЇ & ЮРИСДИКЦІЙ' : isPl ? '🌐 CENTRUM INTEGRACJI TRANSGRANICZNEJ I JURYSDYKCJI' : isDe ? '🌐 GRENZÜBERSCHREITENDES INTEGRATIONSZENTRUM' : isFr ? '🌐 CENTRE D\'INTÉGRATION TRANSFRONTALIÈRE' : '🌐 CROSS-BORDER LAW & INTEROPERABILITY HUB'}
              </span>
            </div>

            <h1 className="text-2xl sm:text-4xl font-black text-white tracking-tight leading-tight">
              {isUk ? 'Гармонізація Законодавства, Е-Фактур та Договорів' : isPl ? 'Harmonizacja Prawa, e-Faktur i Umów Międzynarodowych' : isDe ? 'Harmonisierung von Recht, E-Rechnungen & Verträgen' : isFr ? 'Harmonisation Juridique, E-Factures & Contrats' : 'Cross-Border Legal, Tax & Invoicing Interoperability'}
            </h1>

            <p className="text-slate-300 text-xs sm:text-sm max-w-3xl leading-relaxed">
              {isUk
                ? 'Працюйте у своїй національній нормативній базі (Україна, Польща, Німеччина, Франція, Італія, Іспанія, Швеція, США) з можливістю безшовної адаптації документів, ПДВ (Reverse Charge), корпоративних структур та е-фактур під юрисдикцію іноземного партнера.'
                : isPl
                ? 'Prowadź działalność w oparciu o krajowe przepisy z możliwością natychmiastowej adaptacji umów, podatku VAT (Reverse Charge) oraz e-faktur (KSeF / XRechnung / Factur-X) do prawa zagranicznego kontrahenta.'
                : 'Work within your local legal framework while seamlessly bridging contracts, VAT compliance (Reverse Charge), corporate structures, and e-invoices across 10 international jurisdictions.'}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0">
            <button
              onClick={onBack}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-white/10 hover:bg-white/20 text-white font-bold text-xs border border-white/20 transition-all cursor-pointer shadow-lg hover:shadow-xl"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{isUk ? '← На Головну' : isPl ? '← Do Menu Głównego' : '← Home'}</span>
            </button>
          </div>
        </div>

        {/* Interactive Jurisdiction Matrix Bar */}
        <div className="mt-8 pt-6 border-t border-indigo-500/20 grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
          <div className="md:col-span-5 bg-slate-900/90 border border-indigo-400/30 rounded-2xl p-3.5 space-y-1.5">
            <label className="text-[10px] font-extrabold uppercase tracking-wider text-indigo-300 flex items-center gap-1.5">
              <span>🏛️ {isUk ? 'Ваша Юрисдикція (Країна Походження):' : isPl ? 'Twoja Jurysdykcja (Kraj Pochodzenia):' : 'Source Jurisdiction:'}</span>
            </label>
            <select
              value={sourceCountry}
              onChange={(e) => setSourceCountry(e.target.value)}
              className="w-full bg-slate-950 border border-indigo-500/40 rounded-xl px-3 py-2 text-xs font-bold text-white outline-hidden cursor-pointer"
            >
              {COUNTRIES.map(c => (
                <option key={c.code} value={c.code}>
                  {c.flag} {isPl ? c.namePl : isUk ? c.nameUk : c.nameEn}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2 flex items-center justify-center">
            <div className="w-10 h-10 rounded-2xl bg-indigo-500/20 border border-indigo-400/40 flex items-center justify-center text-indigo-300 shadow-inner">
              <ArrowLeftRight className="w-5 h-5 animate-pulse" />
            </div>
          </div>

          <div className="md:col-span-5 bg-slate-900/90 border border-indigo-400/30 rounded-2xl p-3.5 space-y-1.5">
            <label className="text-[10px] font-extrabold uppercase tracking-wider text-emerald-300 flex items-center gap-1.5">
              <span>🎯 {isUk ? 'Цільова Юрисдикція Партнера / Інтеграції:' : isPl ? 'Jurysdykcja Docelowa Kontrahenta:' : 'Target Jurisdiction:'}</span>
            </label>
            <select
              value={targetCountry}
              onChange={(e) => setTargetCountry(e.target.value)}
              className="w-full bg-slate-950 border border-emerald-500/40 rounded-xl px-3 py-2 text-xs font-bold text-white outline-hidden cursor-pointer"
            >
              {COUNTRIES.map(c => (
                <option key={c.code} value={c.code}>
                  {c.flag} {isPl ? c.namePl : isUk ? c.nameUk : c.nameEn}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Category Tabs & Search Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
        {/* Category Pills */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0 scrollbar-none">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer shrink-0 ${
              selectedCategory === 'all'
                ? 'bg-indigo-600 text-white shadow-md'
                : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
            }`}
          >
            {isUk ? 'Усі Транскордонні Модулі' : isPl ? 'Wszystkie Moduły' : 'All Cross-Border Modules'} ({crossBorderAgents.length})
          </button>

          <button
            onClick={() => setSelectedCategory('contracts')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer shrink-0 flex items-center gap-1.5 ${
              selectedCategory === 'contracts'
                ? 'bg-indigo-600 text-white shadow-md'
                : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
            }`}
          >
            <Scale className="w-3.5 h-3.5" />
            <span>{isUk ? '⚖️ Договори & Арбітраж' : isPl ? '⚖️ Umowy i Arbitraż' : '⚖️ Contracts & Law'}</span>
          </button>

          <button
            onClick={() => setSelectedCategory('vat_invoicing')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer shrink-0 flex items-center gap-1.5 ${
              selectedCategory === 'vat_invoicing'
                ? 'bg-indigo-600 text-white shadow-md'
                : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
            }`}
          >
            <FileCode className="w-3.5 h-3.5" />
            <span>{isUk ? '🧾 Е-Фактури & ПДВ' : isPl ? '🧾 e-Faktury i VAT' : '🧾 VAT & Invoices'}</span>
          </button>

          <button
            onClick={() => setSelectedCategory('corporate')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer shrink-0 flex items-center gap-1.5 ${
              selectedCategory === 'corporate'
                ? 'bg-indigo-600 text-white shadow-md'
                : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
            }`}
          >
            <Building2 className="w-3.5 h-3.5" />
            <span>{isUk ? '🏛️ Корпоративне & Реєстри' : isPl ? '🏛️ Spółki i Rejestry' : '🏛️ Corporate & KYC'}</span>
          </button>
        </div>

        {/* Search Input */}
        <div className="relative w-full sm:w-72 shrink-0">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={isUk ? 'Пошук модуля...' : isPl ? 'Szukaj modułu...' : 'Search module...'}
            className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-xs font-mono text-slate-900 focus:outline-hidden focus:border-indigo-600 shadow-xs"
          />
        </div>
      </div>

      {/* Agents Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAgents.map((agent) => {
          const name = getLocalizedAgentName(agent, activeLang);
          const desc = getLocalizedAgentDesc(agent, activeLang);
          const features = getLocalizedAgentFeatures(agent, activeLang);
          const agentPricing = pricing[agent.id] || { price: 99, singlePrice: 19 };

          return (
            <div
              key={agent.id}
              className="group bg-white border-2 border-slate-200 hover:border-indigo-500 rounded-3xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between relative overflow-hidden"
            >
              {/* Top Accent Gradient */}
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-indigo-500 via-blue-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity" />

              <div className="space-y-4">
                {/* Icon & Category */}
                <div className="flex items-start justify-between gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-indigo-50 border border-indigo-200 flex items-center justify-center text-indigo-700 shadow-xs group-hover:scale-105 transition-transform">
                    <AgentIcon name={agent.icon} className="w-6 h-6" />
                  </div>
                  <div className="text-right">
                    <span className="inline-block px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-200">
                      {isUk ? '🌐 ТРАНСКОРДОННИЙ ШІ' : isPl ? '🌐 TRANSGRANICZNY' : '🌐 CROSS-BORDER'}
                    </span>
                  </div>
                </div>

                {/* Agent Name */}
                <div>
                  <h3 className="text-base font-black text-slate-900 leading-snug group-hover:text-indigo-600 transition-colors line-clamp-2">
                    {name}
                  </h3>
                  <p className="text-xs text-slate-600 mt-2 line-clamp-3 leading-relaxed">
                    {desc}
                  </p>
                </div>

                {/* Key Features List */}
                {features && features.length > 0 && (
                  <div className="pt-2 border-t border-slate-100 space-y-1.5">
                    {features.slice(0, 3).map((feat, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-[11px] text-slate-600 leading-tight">
                        <CheckCircle2 className="w-3.5 h-3.5 text-indigo-600 shrink-0 mt-0.5" />
                        <span className="line-clamp-1">{feat}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Bottom Action & Pricing Bar */}
              <div className="pt-5 mt-4 border-t border-slate-100 flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] text-slate-400 font-bold uppercase">
                    {isUk ? 'Тариф (30 зап):' : isPl ? 'Taryfa (30 zap):' : 'Plan (30 reqs):'}
                  </div>
                  <div className="text-sm font-black text-slate-900">
                    {currency === 'EUR' ? `€${agentPricing.price}` : currency === 'PLN' ? `${agentPricing.price} zł` : currency === 'USD' ? `$${agentPricing.price}` : `${agentPricing.price * 50} грн`}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleOpenPayment(agent.id, name, agentPricing.price, agentPricing.singlePrice)}
                    className="p-2.5 rounded-xl border border-slate-200 hover:border-indigo-400 bg-slate-50 hover:bg-indigo-50 text-indigo-700 transition-all cursor-pointer"
                    title={isUk ? 'Оплатити / Підписка' : isPl ? 'Kup dostęp' : 'Subscribe'}
                  >
                    <CreditCard className="w-4 h-4" />
                  </button>

                  <button
                    type="button"
                    onClick={() => onLaunchAgent(agent.target, activeLang)}
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 active:scale-95 text-white font-extrabold text-xs shadow-md hover:shadow-indigo-500/30 transition-all cursor-pointer"
                  >
                    <span>{isUk ? '🚀 Відкрити' : isPl ? '🚀 Uruchom' : '🚀 Open'}</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Regional Payment Modal */}
      {selectedPaymentAgent && (
        <RegionalPaymentModal
          isOpen={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          agentId={selectedPaymentAgent.id}
          agentName={selectedPaymentAgent.name}
          currency={currency}
          userEmail={currentUserEmail}
          initialPrice={selectedPaymentAgent.price}
          initialSinglePrice={selectedPaymentAgent.singlePrice}
          jurisdiction="EU"
        />
      )}
    </div>
  );
}
